import { isEmpty } from 'lodash';
import moment from 'moment';
import { arrangedLineitems, formatDateOnly } from '../services/common';

// Re-export Vista validation helpers from the single source of truth
export {
  isVistaFlowSatisfied,
  isVistaLineItemFlowSatisfied,
  verifySameCodesForHeaderAndLineItems,
  buildVistaLineItemFields
} from '../services/erpValidationConfig';

export const codingfieldStates = {
  serviceCode: '',
  equipmentNo: '',
  glDivision: '',
  costType: '',
  costId: '',
  jobId: '',
  workOrder: '',
  scope: '',
  phase: '',
  glAccount: '',
  division2: '',
  division3: '',
  division4: '',
  type: ''
};

export const contentFieldsStates = {
  discount: '',
  paymentTerms: ''
};

export const calculateGrossTotal = ({ headerTotal, headerTax, headerMisc, headerFreight } = {}) => {
  const toNumber = (val) => {
    const n = parseFloat(String(val ?? '0')?.replace(/[^0-9.-]+/g, ''));
    return isNaN(n) ? 0 : n;
  };
  return (
    toNumber(headerTotal) - toNumber(headerTax) - toNumber(headerMisc) - toNumber(headerFreight)
  );
};

// ── G702 pay applications: everything comes off `total` ────────────────────────
//
// `total` is the GROSS billed this period — the figure the G703 detail lines sum
// to, what the line items are matched against, and what the FSI export's header
// amount and retainage column are both computed from. Retainage is withheld FROM
// it and the ERP does the subtraction. Current Payment Due is the remainder: a
// read-only figure on the canvas, derived here so it can never disagree with the
// total the user is looking at.
//
// This mirrors `resolveG702Amounts` in the backend's dataFormat.ts, deliberately,
// down to the rounding: the retainage is rounded to the cent FIRST and the net is
// gross minus that, so the two never differ by a cent from the exported file. If
// one rule changes, the other has to follow.

// A percentage, and bounded. Absent, blank, "10%" and — the expensive one — the
// retainage DOLLAR amount off AIA line 5 ("10 % of Completed Work  $12,345",
// which the form prints on one line and OCR reads about as often as the rate) all
// resolve to null. A rate at or above 100 is not a percentage either.
export const parseRetainageRate = (raw) => {
  if (raw === null || raw === undefined) return null;
  const cleaned = String(raw)
    .replace(/[%$,\s]/g, '')
    .trim();
  if (cleaned === '') return null;
  const percent = Number(cleaned);
  if (!Number.isFinite(percent) || percent < 0 || percent >= 100) return null;
  return percent / 100;
};

// A money cell as a number, or null when there is nothing usable in it. Absent
// and zero stay distinct: a pay app with no billed amount must render an empty
// Current Payment Due, not 0.00.
export const parseHeaderMoney = (raw) => {
  if (raw === null || raw === undefined) return null;
  const cleaned = String(raw)
    .replace(/[$,\s]/g, '')
    .trim();
  if (cleaned === '') return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
};

// Current Payment Due, as a 2dp string — '' when there is no total to derive it
// from. Two document types now: a pay application, and a Foundation invoice,
// where a subcontractor's progress billing withholds retainage the same way and
// the approver is signing off what the subcontractor will be paid. One rule for
// both, and it mirrors the backend's `resolveRetainageAmounts` down to the
// rounding — retainage to the cent FIRST, then net = gross − that — so the
// canvas cannot differ from the exported file by a cent.
//
// `fallbackTotal` is the PAY APPLICATION's alone: that cell held the gross on
// every pay app extracted before `total` was captured, and the backend seeds the
// total from it on the read. An invoice must never pass it. Its total is
// extracted directly and this figure is one we wrote from it, so feeding it back
// in compounds the withholding once per render — 28,477.79, 25,630.01,
// 23,067.01, 20,760.31.
export const computeRetainageNetDue = ({ total, retainagePercentage, fallbackTotal } = {}) => {
  const gross = parseHeaderMoney(total) ?? parseHeaderMoney(fallbackTotal);
  if (gross === null) return '';
  const rate = parseRetainageRate(retainagePercentage);
  const retainage = rate !== null && rate > 0 ? Math.round(gross * rate * 100) / 100 : 0;
  return (Math.round((gross - retainage) * 100) / 100).toFixed(2);
};

// ── Freight as a separate line item ─────────────────────────────────────────
//
// `company.freightAsLineItem` plus the Freight field being on in the document
// field configuration. The backend decides the configuration half (it injects
// the Freight row and is the one that knows the configuration), and the header
// `freight` cell is only in `content` when the field is configured — so its
// PRESENCE is the configuration half here, with no second rule to drift. Never a
// column: Display Tax as a Column is a tax-only case.
export const isFreightLineItemEnabled = (company, content) =>
  company?.freightAsLineItem === true &&
  Array.isArray(content) &&
  content.some((item) => item?.name === 'freight');

// Index of the Freight row in the canvas line-item state's Description column,
// -1 when there is none. Same exact-match rule the Tax row is found by.
export const findFreightRowIndex = (descriptions) =>
  Array.isArray(descriptions)
    ? descriptions.findIndex(
        (desc) =>
          String(desc ?? '')
            .trim()
            .toLowerCase() === 'freight'
      )
    : -1;

// Header freight the line-items total check counts toward the expected total.
// On a Foundation DIRECT-WRITE company, freight held only on the header (no
// Freight row) is still part of the invoice: Send to ERP writes it to
// Foundation as its own line. Everywhere else it is 0, deliberately — other
// ERPs' exports carry no header freight, and the Foundation FSI file gets no
// such line (the importer refuses it), so counting it there would let an
// invoice through with its freight missing or one the FSI import cannot take.
export const headerFreightInTotal = ({ erpType, company, content, actualValues, descriptions } = {}) => {
  if (erpType !== 'Foundation' || !company?.useFoundationDirect) return 0;
  const idx = Array.isArray(content) ? content.findIndex((item) => item?.name === 'freight') : -1;
  if (idx < 0 || findFreightRowIndex(descriptions) !== -1) return 0;
  return toNumber(actualValues?.[idx]);
};

// ── Consolidating with a Freight row ────────────────────────────────────────
//
// With "Freight as a Separate Line Item" on, consolidating the line items does
// NOT fold the Freight row into the consolidated row unless the company has
// also switched "Consolidate Freight Line Item" on. The consolidated row
// carries the header total LESS the freight, and the Freight row stays its own
// line beside it, carrying only the coding it was given — no header job,
// equipment or service code is copied onto it.
export const keepsFreightOutOfConsolidation = (company, content) =>
  isFreightLineItemEnabled(company, content) && company?.consolidateFreightLineItem !== true;

const rowCell = (row, name) =>
  Array.isArray(row) ? row.find((cell) => cell?.name === name) : undefined;

export const isFreightRow = (row) =>
  String(rowCell(row, 'Description')?.value ?? '')
    .trim()
    .toLowerCase() === 'freight';

export const isConsolidatedRow = (row) =>
  rowCell(row, 'Description')?.value === 'Consolidated Items';

// A consolidated line-item set: one "Consolidated Items" row, and nothing else
// but Freight rows beside it.
export const isConsolidatedLineItemSet = (rows) =>
  Array.isArray(rows) &&
  rows.filter(isConsolidatedRow).length === 1 &&
  rows.every((row) => isConsolidatedRow(row) || isFreightRow(row));

// Same question asked of the canvas's column state (the Description column).
export const isConsolidatedDescriptionSet = (descriptions) =>
  Array.isArray(descriptions) &&
  descriptions.filter((d) => d === 'Consolidated Items').length === 1 &&
  descriptions.every(
    (d) =>
      d === 'Consolidated Items' ||
      String(d ?? '')
        .trim()
        .toLowerCase() === 'freight'
  );

const freightRowAmount = (row) => {
  const n = parseFloat(String(rowCell(row, 'Amount')?.value ?? '').replace(/[^0-9.-]/g, ''));
  return isNaN(n) ? 0 : n;
};

// Split the rows to consolidate into the Freight rows and the rest. When the
// rows carry no Freight row but the header has freight, one is made from the
// header amount, so the freight is never lost into the consolidated row.
// `keepFreight` false hands every row back to be consolidated, as before.
export const splitFreightForConsolidation = (rows, { keepFreight, headerFreight } = {}) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!keepFreight) return { otherRows: list, freightRows: [], freightTotal: 0 };
  const otherRows = list.filter((row) => !isFreightRow(row));
  let freightRows = list.filter(isFreightRow);
  if (freightRows.length === 0) {
    const amount = freightRowAmountFromHeader(headerFreight);
    if (amount !== null && parseFloat(amount) !== 0) {
      freightRows = [
        [
          { name: 'Description', value: 'Freight', width: null, height: null, left: null },
          { name: 'Amount', value: amount, width: null, height: null, left: null }
        ]
      ];
    }
  }
  const freightTotal = freightRows.reduce((sum, row) => sum + freightRowAmount(row), 0);
  return { otherRows, freightRows, freightTotal };
};

// The consolidated row's amount once the freight is its own line: the amount
// it would have carried, less the freight. A value that is not a number (blank,
// OCR's '-') is handed back untouched, as is anything when there is no freight.
export const consolidatedAmountLessFreight = (amount, freightTotal) => {
  if (!freightTotal) return amount;
  const n = parseFloat(String(amount ?? '').replace(/[^0-9.-]/g, ''));
  if (isNaN(n)) return amount;
  return (Math.round((n - freightTotal) * 100) / 100).toFixed(2);
};

// A Freight row given the consolidated row's columns, so the grid renders both
// alike. Each column keeps the Freight row's OWN value, or blank — never the
// header's coding the consolidated row was filled with.
export const shapeFreightRowLike = (freightRow, consolidatedRow) => {
  if (!Array.isArray(consolidatedRow) || consolidatedRow.length === 0) return freightRow;
  return consolidatedRow.map((cell) => {
    const own = rowCell(freightRow, cell?.name);
    return {
      ...cell,
      value: own?.value ?? '',
      width: own?.width ?? '',
      height: own?.height ?? '',
      left: own?.left ?? ''
    };
  });
};

// The consolidated line items. `consolidate(otherRows, freightTotal)` builds the
// one consolidated row (the caller's existing consolidateItems call, with its
// amount reduced by `freightTotal`); the Freight rows follow it, each shaped
// like it. With `keepFreight` false this is exactly [consolidate(rows, 0)].
export const consolidateKeepingFreight = ({ rows, keepFreight, headerFreight, consolidate }) => {
  const { otherRows, freightRows, freightTotal } = splitFreightForConsolidation(rows, {
    keepFreight,
    headerFreight
  });
  const consolidatedRow = consolidate(otherRows, freightTotal);
  return [consolidatedRow, ...freightRows.map((row) => shapeFreightRowLike(row, consolidatedRow))];
};

// The Amount to copy from a header freight edit onto the Freight row, or null
// when the input is not a number (the user is mid-typing "$" or similar).
export const freightRowAmountFromHeader = (rawValue) => {
  const cleaned = String(rawValue ?? '').replace(/[^0-9.-]/g, '');
  if (cleaned === '' || Number.isNaN(parseFloat(cleaned))) return null;
  return formatDistributedValue(parseFloat(cleaned));
};

// Vista Tax Type 2 = use / self-assessed tax: the vendor does not charge tax on the
// invoice, so the header total excludes tax (header total == line-item subtotal). Tax
// Type 1 (sales tax) is added to the total as before. Match the leading integer so both
// a bare '2' and a labeled '2 - Use Tax' resolve to use tax.
export const isUseTaxType = (taxType) => parseInt(String(taxType ?? '').trim(), 10) === 2;

// ── The amounts the Vista PO/SC check (validate-po) is asked about ──────────────
//
// One place, because the answer must not depend on how a cell happens to be
// formatted. The check compares the invoice's GROSS (total - tax - misc - freight)
// against what is left on the commitment and flags an over-billing — a hard stop on
// Approve — so a gross that is wrong by the tax is a false red flag on a good invoice.
//
// That is exactly what a currency sign did. The tax was judged "empty" with
// `isNaN(parseFloat(raw))`, and `parseFloat('$12.50')` is NaN: a tax OCR read with
// its "$" counted as no tax, was not subtracted, and the gross reached the backend too
// high by the whole tax. Deleting the "$" made the flag go away, which is the
// customer report. `parseFloat('1,234.50')` is 1, so a grouped tax was sent as $1.
//
// The cleaning is the same rule the initial document load has always applied to the
// tax (`taxFromData` in canvas.jsx), so the load and every later re-check agree.
// Total, misc and freight need nothing extra: calculateGrossTotal already strips them.
export const cleanAmountInput = (value) =>
  value === null || value === undefined ? '' : String(value).replace(/[^0-9.-]+/g, '');

export const resolvePoCheckAmounts = ({ total, tax, taxType, misc, freight } = {}) => {
  const cleanedTax = cleanAmountInput(tax);
  // Empty also covers OCR's "-" (not found) and a cell that is only a currency sign.
  const taxIsEmpty = cleanedTax === '' || isNaN(parseFloat(cleanedTax));
  const gross = calculateGrossTotal({
    headerTotal: total,
    // Use tax (type 2) is never part of the header total, so it is not netted out.
    headerTax: taxIsEmpty || isUseTaxType(taxType) ? 0 : cleanedTax,
    headerMisc: misc,
    headerFreight: freight
  });
  return {
    taxIsEmpty,
    // What the backend tax check is sent: a plain number string, or 0 for no tax.
    tax: taxIsEmpty ? 0 : cleanedTax,
    gross: String(gross)
  };
};

// Amount to place on the consolidated line-item row. For Vista with tax calculation
// enabled the row holds the pre-tax (gross) subtotal while tax/misc sit in their own
// columns: when line items exist we use their summed amount, and when there are none we
// derive that subtotal from the header (total minus tax/misc/freight) so the row plus its
// tax/misc columns still reconcile to the header total. Otherwise the row equals the
// header total (tax dropped — same as Foundation). Shared by the initial-load, consolidate
// toggle, and reset paths so all three behave identically.
export const getConsolidatedRowAmount = ({
  erpType,
  taxCalculationPermission,
  displayTaxAsColumn,
  miscPermission,
  lineItemSum = 0,
  getHeaderVal,
  headerTotalFallback,
  isUseTax = false
} = {}) => {
  if (erpType === 'viewPointVista' && taxCalculationPermission) {
    if (Number(lineItemSum) > 0) return String(Number(lineItemSum).toFixed(2));
    const gross = calculateGrossTotal({
      headerTotal: getHeaderVal?.('total') || getHeaderVal?.('current_payment_due'),
      // Use tax (type 2) is never part of the header total, so don't net it back out.
      headerTax: displayTaxAsColumn && !isUseTax ? getHeaderVal?.('tax') : 0,
      headerMisc: miscPermission ? getHeaderVal?.('miscellaneous_amount') : 0,
      headerFreight: getHeaderVal?.('freight')
    });
    // Always the gross — even when user-entered tax/misc push it negative; the row plus
    // its tax/misc columns then still reconciles to the header total (e.g. -4.90 + 0.38 +
    // 10 = 5.48). The recalc effect distributes tax/misc onto the row regardless of sign.
    return String(gross.toFixed(2));
  }
  return headerTotalFallback;
};

// Format a distributed line-item value (e.g. tax, misc) with 2–3 decimals:
// keep 2 when that's all it has, allow up to 3, but never force a trailing
// third zero (so 7.5 -> "7.50", 7.125 -> "7.125", 7 -> "7.00").
export const formatDistributedValue = (num) => {
  const n = parseFloat(num);
  if (isNaN(n)) return '';
  const capped = parseFloat(n.toFixed(3));
  const decimals = (String(capped).split('.')[1] || '').length;
  return capped.toFixed(Math.max(2, decimals));
};

// Decimal precision to distribute a header value (tax/misc) across line items:
// match the header value's own decimal places — rounded to 3 first to absorb
// float noise — clamped to a minimum of 2 and a maximum of 3. So a header tax
// of 2 decimals -> 2-decimal line items, a 3-decimal header tax -> up to 3.
export const getDistributionPrecision = (value) => {
  const n = parseFloat(String(value ?? '').replace(/[^0-9.-]+/g, ''));
  if (isNaN(n)) return 2;
  const decimals = (String(parseFloat(n.toFixed(3))).split('.')[1] || '').length;
  return Math.min(3, Math.max(2, decimals));
};

export const calculateHeaderTotalWithoutTax = (lineItemsCopy, headerTotalStr) => {
  const toNumber = (val = '') => Number(String(val)?.replace(/[^0-9.-]/g, ''));

  // Header total
  const headerTotal = toNumber(headerTotalStr);

  // Find tax row
  const taxRow = lineItemsCopy?.find(
    (row) => row?.find((item) => item?.name === 'Description')?.value?.toLowerCase() === 'tax'
  );

  const taxAmountStr = taxRow?.find((item) => item?.name === 'Amount')?.value || '0';
  const taxAmount = toNumber(taxAmountStr);

  // Sum ALL line items (including tax)
  const lineItemsTotal = lineItemsCopy?.reduce((sum, row) => {
    const amt = row?.find((item) => item?.name === 'Amount')?.value;
    return sum + toNumber(amt);
  }, 0);

  let finalTotal = headerTotal;

  // Only subtract tax if header >= line items total
  if (headerTotal >= lineItemsTotal && taxAmount > 0) {
    finalTotal = headerTotal - taxAmount;
  }

  return String(finalTotal);
};
export function getFirstValue(str) {
  const match = str?.match(/^\d+/);
  return match ? match[0] : str;
}

// ── validate-po budget-code check (Vista) ─────────────────────────────────────────────
// validate-po also verifies that the job / phase / cost type coding is still ACTIVE in the
// company budget — at the header (`codeContent`) and per line item. The response mirrors
// that split: `poBudgetMatch.header` and `poBudgetMatch.lineItems[i]`, each carrying one
// `{ value, active }` object per code. Response key → the field name the header coding and
// line-item columns are rendered under, so a verdict can be resolved straight from a field.
// Cost id is deliberately absent: it is sent with the request but the check never reports on
// it, so it can never be flagged inactive — see PO_VALIDATION_CODE_CONTENT_FIELDS.
export const PO_BUDGET_CODE_FIELDS = {
  jobId: 'userJobId',
  phase: 'userPhases',
  costType: 'userCostType'
};

// The header codes sent as `codeContent`. Cost id rides along so the BE sees the complete
// header coding, even though only the three codes above come back with an active verdict.
export const PO_VALIDATION_CODE_CONTENT_FIELDS = [
  ...Object.values(PO_BUDGET_CODE_FIELDS),
  'userCostId'
];

// Header coding sent as `codeContent`. Every code is always sent (empty string when the
// document has no value for it) so the BE sees the full header coding, not just what is set.
export const buildPoValidationCodeContent = (accountCodeArray = [], actualaccountCodeValues = []) =>
  PO_VALIDATION_CODE_CONTENT_FIELDS?.map((name) => {
    const idx = accountCodeArray?.findIndex((item) => item?.name === name);
    return { name, value: (idx >= 0 ? actualaccountCodeValues?.[idx] : '') ?? '' };
  });

// Payload key → the `obj` (column-oriented line items) key it is read from.
const PO_VALIDATION_LINE_ITEM_COLUMNS = {
  description: 'Description',
  qty: 'Quantity',
  unit_price: 'Unit_Price',
  total: 'Amount',
  jobId: 'userJobId',
  phase: 'userPhases',
  costType: 'userCostType'
};

// Line items sent with validate-po. EVERY row goes, in display order and unfiltered (unlike
// the PO match call, which drops the tax row): `poBudgetMatch.lineItems` comes back indexed
// the same way, and that index is what maps a verdict back to its row.
export const buildPoValidationLineItems = (obj = {}) => {
  const entries = Object.entries(PO_VALIDATION_LINE_ITEM_COLUMNS);
  const rowCount = entries?.reduce(
    (max, [, column]) => Math.max(max, Array.isArray(obj?.[column]) ? obj[column].length : 0),
    0
  );
  return Array.from({ length: rowCount }, (_, index) =>
    entries?.reduce((row, [key, column]) => {
      row[key] = obj?.[column]?.[index] ?? '';
      return row;
    }, {})
  );
};

// Same payload as above, built from the RAW row-oriented line items (`[[{name, value}, …], …]`)
// instead of the column-oriented `obj`. Needed on the initial document load: `obj` is derived
// from `lineItem` in a later effect, so the load-time closure still holds the previous
// document's columns (empty on a fresh mount) and would send no line items at all. Row order is
// preserved, so `poBudgetMatch.lineItems[i]` still maps back to row i once `obj` settles.
export const buildPoValidationLineItemsFromRows = (rows = []) =>
  (Array.isArray(rows) ? rows : [])?.map((row) =>
    Object.entries(PO_VALIDATION_LINE_ITEM_COLUMNS)?.reduce((item, [key, column]) => {
      item[key] = row?.find?.((field) => field?.name === column)?.value ?? '';
      return item;
    }, {})
  );

// The inactive-code verdict for one field of one `poBudgetMatch` entry (header or a single
// line item), or '' when there is nothing to flag. Only an explicit `active: false` counts —
// `skipped` means the BE could not check the budget at all, which is not a failed check, and
// a missing code object means that code isn't part of the budget check. A field outside
// PO_BUDGET_CODE_FIELDS (cost id, gl account, …) is never flagged, whatever the entry holds.
export const getPoBudgetCodeError = (entry, fieldName, label) => {
  if (!entry || entry?.skipped === true) return '';
  const responseKey = Object.keys(PO_BUDGET_CODE_FIELDS)?.find(
    (key) => PO_BUDGET_CODE_FIELDS[key] === fieldName
  );
  if (!responseKey) return '';
  const code = entry?.[responseKey];
  if (!code || code?.active !== false) return '';
  // Just the field's own label — "Inactive Cost Type", not "Inactive Cost Type code".
  return `Inactive ${label || fieldName}`;
};

// PO -> Job Mapping (company flag `poToJobMapping`): the BE moves a PO that is really a job
// number into the job field. `document.poJobMapping` is optimistic — `{status: true, message: ''}`
// for a flag-off company, a document with no PO and a PO that did resolve — so `status === false`
// means the mapping was asked to resolve a PO into a job and could not. Surfaced as a warning on
// the job field, and only while that field is still empty: once a job is picked the miss is moot.
export const getPoJobMappingWarning = (poJobMapping, isPoToJobMappingOn, jobValue) => {
  if (!isPoToJobMappingOn) return '';
  if (poJobMapping?.status !== false) return '';
  if (String(jobValue ?? '')?.trim() !== '') return '';
  return poJobMapping?.message || '';
};

// "Expense Based Coding" (company flag `expenseBasedCoding`): an Expense-typed Vista document is
// routed to reviewers by its Reviewer Group, and with no group Vista routes it to nobody. So an
// empty group on a `3-Exp` document is worth saying out loud at approval time.
//
// A WARNING, never an error — it must not reach `validationErrors`, which feeds `hasErrors` and
// disables Approve. There are legitimate reasons the group is blank (the GL account genuinely has
// none, the sync has not run), and blocking the approve for them would be worse than the routing
// gap it is warning about.
//
// Gated on the batch type as well as the flag: the APPROVED Vista object has no ReviewerGroup key
// at all, so an Approved company would otherwise carry a warning it could do nothing about. The
// frontend spells the value 'UnApproved' and the backend spells it 'unApproved', hence the
// case-insensitive compare.
export const REVIEWER_GROUP_EXPENSE_WARNING =
  'No Reviewer Group. An Expense line with no reviewer group is not routed to anyone in Vista — pick one, or check that this GL account has a reviewer group in the ERP.';

export const getReviewerGroupWarning = (
  isExpenseBasedCodingOn,
  erpType,
  vistaBatchType,
  typeValue,
  reviewerGroupValue
) => {
  if (!isExpenseBasedCodingOn || erpType !== 'viewPointVista') return '';
  if (String(vistaBatchType ?? '').toLowerCase() !== 'unapproved') return '';
  if (String(typeValue ?? '').trim() !== '3-Exp') return '';
  if (String(reviewerGroupValue ?? '').trim() !== '') return '';
  return REVIEWER_GROUP_EXPENSE_WARNING;
};

// Does the document carry ANY inactive code, header or line item? An inactive code is a hard
// stop — the coding cannot post against a budget that no longer holds it — so this drives the
// approval block, using exactly the same per-code rule the field badges are drawn from.
export const hasInactivePoBudgetCodes = (poBudgetMatch) => {
  if (!poBudgetMatch) return false;
  const entries = [poBudgetMatch?.header, ...(poBudgetMatch?.lineItems || [])];
  return entries?.some((entry) =>
    Object.values(PO_BUDGET_CODE_FIELDS)?.some((fieldName) =>
      Boolean(getPoBudgetCodeError(entry, fieldName, fieldName))
    )
  );
};

const VISTA_DUE_DATE_FORMAT = 'MM/DD/YYYY';
const VISTA_INVOICE_DATE_FORMATS = [
  'MM/DD/YYYY',
  'M/D/YYYY',
  'YYYY-MM-DD',
  'MM-DD-YYYY',
  'M-D-YYYY'
];

export const parseVistaPaymentTerm = (termStr) => {
  if (typeof termStr !== 'string') return null;
  const raw = termStr.trim();
  if (raw === '') return null;
  const term = raw.toUpperCase();

  if (/^0+\s*-\s*0+$/.test(term)) return { type: 'onReceipt' };
  if (/\b(?:DUE\s+)?(?:UPON|ON)\s+RECEIPT\b/.test(term)) return { type: 'onReceipt' };
  if (term === 'COD') return { type: 'onReceipt' };
  if (term === 'EOM' || term === 'END OF MONTH') return { type: 'eom' };

  const netDays = term.match(/\bNET\s+(\d{1,3})\s*(?:DAYS?)?\b(?!\s*TH?)/);
  if (netDays) return { type: 'net', days: parseInt(netDays[1], 10) };

  const nShort = term.match(/^N\s*(\d{1,3})$/);
  if (nShort) return { type: 'net', days: parseInt(nShort[1], 10) };

  // Day-of-month: accept both 'TH' and 'T' (Vista sometimes stores the abbreviated form).
  const netDay = term.match(/\bNET\s+(\d{1,2})\s*TH?\b/);
  if (netDay) {
    const discountMatch = term.match(/(\d+(?:\.\d+)?)\s*%\s*(\d{1,2})\s*TH?\b/);
    const proximo = !!discountMatch;
    const result = { type: 'dayOfMonth', day: parseInt(netDay[1], 10), proximo };
    if (discountMatch) {
      result.discountPercent = parseFloat(discountMatch[1]);
      result.discountDay = parseInt(discountMatch[2], 10);
    }
    return result;
  }

  const discountDay = term.match(/^\d+\s*%\s*(\d{1,2})\s*TH?$/);
  if (discountDay) return { type: 'dayOfMonth', day: parseInt(discountDay[1], 10) };

  const dashPair = term.match(/^(\d{1,3})\s*-\s*(\d{1,3})$/);
  if (dashPair) return { type: 'net', days: parseInt(dashPair[1], 10) };

  const withinDays = term.match(/\bWITHIN\s+(\d{1,3})\s+DAYS?\b/);
  if (withinDays) return { type: 'net', days: parseInt(withinDays[1], 10) };

  const anyDays = term.match(/\b(\d{1,3})\s+DAYS?\b/);
  if (anyDays) return { type: 'net', days: parseInt(anyDays[1], 10) };

  return null;
};

const parseInvoiceMoment = (invoiceDate) => {
  if (!invoiceDate) return null;
  const m =
    invoiceDate instanceof Date
      ? moment(invoiceDate)
      : moment(String(invoiceDate), VISTA_INVOICE_DATE_FORMATS, true);
  return m && m.isValid() ? m : null;
};

export const isValidVistaDateString = (value) => parseInvoiceMoment(value) !== null;

const dayOfMonthDue = (invoice, day, proximo = false) => {
  if (day > 31) {
    return invoice
      .clone()
      .add(1, 'month')
      .date(Math.min(day - 31, invoice.clone().add(1, 'month').daysInMonth()));
  }
  if (proximo) {
    const next = invoice.clone().add(1, 'month');
    return next.date(Math.min(day, next.daysInMonth()));
  }
  const sameMonth = invoice.clone().date(Math.min(day, invoice.daysInMonth()));
  if (sameMonth.isAfter(invoice, 'day')) return sameMonth;
  const next = invoice.clone().add(1, 'month');
  return next.date(Math.min(day, next.daysInMonth()));
};

export const calculateVistaDueDate = (invoiceDate, termStr) => {
  const invoice = parseInvoiceMoment(invoiceDate);
  if (!invoice) return '';
  const descriptor = parseVistaPaymentTerm(termStr);
  if (!descriptor) return '';

  switch (descriptor.type) {
    case 'onReceipt':
      return invoice.format(VISTA_DUE_DATE_FORMAT);
    case 'net':
      return invoice.clone().add(descriptor.days, 'days').format(VISTA_DUE_DATE_FORMAT);
    case 'eom':
      return invoice.clone().endOf('month').format(VISTA_DUE_DATE_FORMAT);
    case 'dayOfMonth':
      return dayOfMonthDue(invoice, descriptor.day, descriptor.proximo).format(
        VISTA_DUE_DATE_FORMAT
      );
    default:
      return '';
  }
};

export const calculateVistaDiscountDate = (invoiceDate, termStr) => {
  const invoice = parseInvoiceMoment(invoiceDate);
  if (!invoice) return '';
  const descriptor = parseVistaPaymentTerm(termStr);
  if (!descriptor || descriptor.discountDay == null) return '';
  return dayOfMonthDue(invoice, descriptor.discountDay, descriptor.proximo).format(
    VISTA_DUE_DATE_FORMAT
  );
};

// Transaction Date value for companies running with `transactionDateAsCurrentDate`:
// today instead of the invoice date the extraction pre-populated it with. Vista's GL
// Date is month/year only (and must be the 1st of a month), so it snaps to the 1st of
// the current month. Returns the same MM-DD-YYYY string the date fields use.
export const getCurrentTransactionDate = (erpType, today = new Date()) =>
  formatDateOnly(
    erpType === 'viewPointVista' ? new Date(today.getFullYear(), today.getMonth(), 1) : today
  );

// WHICH company flag decides that, per document type.
//
// A pay application asks separately from an invoice: `transactionDateAsCurrentDate`
// governs every other document type and `g702TransactionDateAsCurrentDate` governs
// the G702 alone. The two are independent in both directions and neither implies
// the other — a company that wants today's date on the invoices it receives daily
// does not necessarily want it on a monthly pay application, whose posting period
// is the thing the billing is FOR.
//
// The stamping itself is identical once the answer is yes, which is why this is a
// flag lookup and not a second copy of the behaviour.
export const usesCurrentTransactionDate = (documentType, company) =>
  Boolean(
    documentType === 'G702'
      ? company?.g702TransactionDateAsCurrentDate
      : company?.transactionDateAsCurrentDate
  );

// "Export Date as Transaction Date": the posting date of an invoice or receipt is
// decided when it is EXPORTED — the backend writes the export date into the file /
// ERP payload (api/utilities/exportTransactionDateUtility.ts). On the canvas that
// makes the field read-only, never stamped with today, and never validated: nobody
// can change it and its value is not what gets sent. Invoices and receipts only,
// the same list the backend applies (EXPORT_DATE_DOCUMENT_TYPES): the G702 posts
// for its billing period and keeps its own switch.
export const EXPORT_DATE_DOCUMENT_TYPES = ['Invoice', 'Receipt'];
export const usesExportDateAsTransactionDate = (documentType, company) =>
  company?.exportDateAsTransactionDate === true &&
  EXPORT_DATE_DOCUMENT_TYPES.includes(documentType);

// WHETHER to stamp it on the document in hand.
//
// The stamp is a PRE-FILL of the extracted value, so it may only ever run on a
// document no person has written to yet. Once somebody has, the stored date is
// their decision and re-stamping it silently overrules them — AP sets the posting
// date deliberately, hands the document on, and the next approver must see what AP
// chose, not today.
//
// Two ways a person has written to it, and both are needed:
//
//   `saveState` — true only after a Save draft. Every APPROVAL path writes it
//   false (documentQueriesV2 updateContentV3 / updateDocumentAutoApproval), so on
//   its own it does not survive AP approving the document.
//
//   `approvers` — pushed on every approval, so a non-empty one means the document
//   has been through somebody even though saveState came back false.
//
// No document is not a fresh document: with nothing in hand there is nothing to
// pre-fill, and an absent `documentType` would otherwise fall through to the
// invoice flag and read as untouched on both counts.
//
// Never under "Export Date as Transaction Date", which wins where both are on: the
// export overwrites whatever is stored, so a stamp would only show a date that is
// not the one sent.
export const shouldStampCurrentTransactionDate = (document, company) =>
  Boolean(document) &&
  usesCurrentTransactionDate(document?.documentType, company) &&
  !usesExportDateAsTransactionDate(document?.documentType, company) &&
  !document?.saveState &&
  isEmpty(document?.approvers);

export const getVistaDiscountPercent = (termStr) => {
  const descriptor = parseVistaPaymentTerm(termStr);
  if (!descriptor || descriptor.discountPercent == null) return '';
  return descriptor.discountPercent;
};

// Computes the Vista payment-term autofill diff: due_date, discount_date, discount.
// Returns null when nothing needs to change. Caller is responsible for applying the
// patches to actualValues / contentFields and gating on saveState.
//
// `recompute` should be true only when invoice_date or payment_term just changed.
// When false (initial load), fields are only filled if empty — existing values are
// preserved. When true (user-driven change), all three fields are kept in sync with
// the new term, including being cleared when the new term has no discount.
//
// If the term doesn't parse (calculateVistaDueDate returns ''), nothing is
// overwritten — partial typing shouldn't wipe previously valid values.
export const computeVistaPaymentTermAutofill = ({
  content,
  actualValues,
  contentFields,
  invoiceDate,
  paymentTerm,
  recompute = false
}) => {
  if (!Array.isArray(content) || !content.length) return null;
  if (!Array.isArray(actualValues) || !actualValues.length) return null;
  if (!invoiceDate || !paymentTerm) return null;

  const dueDateIndex = content.findIndex((item) => item?.name === 'due_date');
  if (dueDateIndex === -1) return null;

  const dueDate = calculateVistaDueDate(invoiceDate, paymentTerm);
  const discountDate = calculateVistaDiscountDate(invoiceDate, paymentTerm);
  const discountPercent = getVistaDiscountPercent(paymentTerm);
  // Vista's discount field holds a flat amount (no "%"). Convert the payment term's
  // discount percent to a dollar amount against the pre-tax subtotal (total − tax − misc −
  // freight). If the subtotal isn't known yet, leave discount empty rather than write "0".
  const getHeaderNum = (name) => {
    const idx = content.findIndex((c) => c?.name === name);
    return idx >= 0 ? actualValues[idx] : 0;
  };
  const discountBase = calculateGrossTotal({
    headerTotal: getHeaderNum('total') || getHeaderNum('current_payment_due'),
    headerTax: getHeaderNum('tax'),
    headerMisc: getHeaderNum('miscellaneous_amount'),
    headerFreight: getHeaderNum('freight')
  });
  const discountValue =
    discountPercent !== '' && discountBase > 0
      ? String(parseFloat(((discountPercent / 100) * discountBase).toFixed(2)))
      : '';

  const discountDateIndex = content.findIndex((item) => item?.name === 'discount_date');
  const discountIndex = content.findIndex((item) => item?.name === 'discount');
  const existingDueDate = String(actualValues[dueDateIndex] ?? '').trim();
  const existingDiscountDate = String(actualValues[discountDateIndex] ?? '').trim();
  const existingDiscountCell = String(actualValues[discountIndex] ?? '').trim();
  const existingDiscountField = String(contentFields?.discount ?? '').trim();

  // Overwriting/clearing is only safe when the term parsed successfully — otherwise
  // we'd wipe valid data while the user is mid-typing.
  const canOverwrite = recompute && !!dueDate;

  const fillDueDate =
    !!dueDate && (existingDueDate === '' || (canOverwrite && existingDueDate !== dueDate));
  const fillDiscountDate =
    discountDateIndex !== -1 &&
    ((!!discountDate && existingDiscountDate === '') ||
      (canOverwrite && existingDiscountDate !== discountDate));
  const fillDiscount =
    discountIndex !== -1 &&
    ((!!discountValue && existingDiscountCell === '') ||
      (canOverwrite && existingDiscountCell !== discountValue));
  const fillDiscountField =
    (!!discountValue && existingDiscountField === '') ||
    (canOverwrite && existingDiscountField !== discountValue);

  if (!fillDueDate && !fillDiscountDate && !fillDiscount && !fillDiscountField) return null;

  const actualValuesPatch = {};
  if (fillDueDate) actualValuesPatch[dueDateIndex] = dueDate;
  if (fillDiscountDate) actualValuesPatch[discountDateIndex] = discountDate;
  if (fillDiscount) actualValuesPatch[discountIndex] = discountValue;

  return {
    actualValuesPatch: Object.keys(actualValuesPatch).length ? actualValuesPatch : null,
    contentFieldsPatch: fillDiscountField ? { discount: discountValue } : null
  };
};

// Collect the Vista "Pay Control" options out of the synced coding data. Every synced
// vendor carries an `accountant` (its pay control) which may be blank, and gl-info also
// returns them as a flat `accountant` array — both shapes are accepted, trimmed,
// de-duplicated (case-insensitively, first spelling wins) and sorted.
export const getPayControlOptions = (glInfo = {}, extraNames = []) => {
  const flatList = Array.isArray(glInfo?.accountant) ? glInfo.accountant : [];
  const fromVendors = (glInfo?.vendor || [])?.map((vendor) => vendor?.accountant);
  const seen = new Map();
  [...flatList, ...fromVendors, ...(extraNames || [])]?.forEach((name) => {
    const trimmed = String(name ?? '')?.trim();
    if (trimmed && !seen.has(trimmed?.toLowerCase())) seen.set(trimmed?.toLowerCase(), trimmed);
  });
  return Array.from(seen.values())?.sort((a, b) => a.localeCompare(b));
};

// The pay control of ONE synced vendor, matched by ERP vendor number. Returns '' when
// the vendor isn't on the synced list or was synced without a pay control.
export const getVendorPayControl = (glInfo = {}, vendorNumber) => {
  if (!vendorNumber) return '';
  const match = (glInfo?.vendor || []).find(
    (vendor) => String(vendor?.id ?? '') === String(vendorNumber)
  );
  return String(match?.accountant ?? '').trim();
};

// Parse a currency-ish string/number into a finite number (0 on failure).
export const toNumber = (val) => {
  const n = parseFloat(String(val ?? '0').replace(/[^0-9.-]+/g, ''));
  return isNaN(n) ? 0 : n;
};

// Resolve a header discount ("2%" or a flat amount) to a dollar amount. A "%" value
// is applied to `base` (the pre-discount subtotal); a plain number is taken as-is.
// Returns a finite number (0 on empty/invalid). Note: toNumber alone can't be used on
// a "%" value — it would strip the "%" and read "2%" as $2 instead of 2% of the base.
export const resolveDiscountAmount = (rawDiscount, base = 0) => {
  const raw = String(rawDiscount ?? '').trim();
  if (raw === '') return 0;
  if (raw.includes('%')) return (toNumber(raw) / 100) * toNumber(base);
  return toNumber(raw);
};

// Distribute `headerVal` across `amounts` proportionally to each amount's share of
// `grossTotal`, rounding each to `precision` decimals (default 2) and pushing the
// rounding remainder onto the last row so the parts always sum back to `headerVal`.
// Returns numbers.
export const distributeProportionally = (amounts = [], grossTotal, headerVal, precision = 2) => {
  const values = amounts.map((amt) =>
    parseFloat(((toNumber(amt) / grossTotal) * headerVal).toFixed(precision))
  );
  const diff = parseFloat((headerVal - values.reduce((a, b) => a + b, 0)).toFixed(precision));
  if (values.length > 0) {
    values[values.length - 1] = parseFloat((values[values.length - 1] + diff).toFixed(precision));
  }
  return values;
};

// Trigger a browser download for an already-signed URL.
export const downloadFileFromUrl = (s3FileName, signedUrl) => {
  const link = document.createElement('a');
  link.href = signedUrl;
  link.setAttribute('download', s3FileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Add per-line-item `tax` / `miscellaneous_amount` / `discount` columns by proportionally
// distributing the header-level tax/misc/discount totals across the rows. The legacy "tax"
// description row is dropped when tax-as-column is active. Rows that already carry every
// applicable value (e.g. PO-fetched rows) are left untouched.
export const applyTaxAndMiscColumns = ({
  rawLineItems,
  standardContent = [],
  isTaxPerm,
  isMiscPerm,
  isDiscountPerm = false,
  isUseTax = false
}) => {
  if (!isTaxPerm && !isMiscPerm && !isDiscountPerm) return rawLineItems;

  const getHeaderVal = (name) => toNumber(standardContent.find((c) => c.name === name)?.value);
  const getHeaderRaw = (name) => standardContent.find((c) => c.name === name)?.value;
  const headerTax = isTaxPerm ? getHeaderVal('tax') : 0;
  const headerMisc = isMiscPerm ? getHeaderVal('miscellaneous_amount') : 0;
  const grossTotal = calculateGrossTotal({
    headerTotal: getHeaderVal('total') || getHeaderVal('current_payment_due'),
    // Use tax (type 2) is not in the header total, so the pre-tax subtotal (the
    // distribution denominator) must not subtract it; tax is still distributed below.
    headerTax: isUseTax ? 0 : headerTax,
    headerMisc,
    headerFreight: getHeaderVal('freight')
  });
  // Discount is a percent ("2%") or a flat amount; resolve it against the pre-tax
  // subtotal. It is NOT netted out of grossTotal (it doesn't reduce the invoice total).
  const headerDiscount = isDiscountPerm
    ? resolveDiscountAmount(getHeaderRaw('discount'), grossTotal)
    : 0;

  const isTaxDescriptionRow = (row) =>
    row?.find((f) => f.name === 'Description')?.value?.toLowerCase() === 'tax';

  // Tax-as-column drops the legacy "tax" description row entirely (tax becomes a column);
  // when it's off the tax row stays as its own line item.
  const withoutTaxRow = isTaxPerm
    ? rawLineItems?.filter((row) => !isTaxDescriptionRow(row)) ?? rawLineItems
    : rawLineItems;

  // The standalone tax line item (tax-as-column off) is never part of the distribution: it
  // must not receive a tax/misc/discount share, nor dilute the base (nor absorb the diff
  // correction, which lands on the last distributed row). When tax IS a column the row was
  // already removed above, so this only changes the tax-as-column-off case.
  const distributionRows =
    withoutTaxRow?.filter((row) => !isTaxDescriptionRow(row)) ?? withoutTaxRow;

  const distribute = (rows, headerVal) =>
    distributeProportionally(
      rows.map((row) => toNumber(row?.find((f) => f.name === 'Amount')?.value)),
      grossTotal,
      headerVal,
      getDistributionPrecision(headerVal)
    );

  const taxProportions = isTaxPerm && grossTotal > 0 ? distribute(distributionRows, headerTax) : [];
  const miscProportions =
    isMiscPerm && grossTotal > 0 ? distribute(distributionRows, headerMisc) : [];
  const discountProportions =
    isDiscountPerm && grossTotal > 0 ? distribute(distributionRows, headerDiscount) : [];

  let distRowIdx = 0;
  return (
    withoutTaxRow?.map((row) => {
      // The tax line item is skipped in the distribution index so its share stays empty;
      // every other row consumes the next proportion in order (aligned to distributionRows).
      const proportionIdx = isTaxDescriptionRow(row) ? -1 : distRowIdx++;

      const existingTax = row?.find((f) => f.name === 'tax');
      const existingMisc = row?.find((f) => f.name === 'miscellaneous_amount');
      const existingDiscount = row?.find((f) => f.name === 'discount');
      // Rows that already carry every applicable value (e.g. PO-fetched) — keep them as-is
      const taxDone = !isTaxPerm || existingTax !== undefined;
      const miscDone = !isMiscPerm || existingMisc !== undefined;
      const discountDone = !isDiscountPerm || existingDiscount !== undefined;
      if (taxDone && miscDone && discountDone) return row;

      const newRow = [...row];
      if (isTaxPerm && existingTax === undefined) {
        const val = taxProportions[proportionIdx] ?? 0;
        newRow.push({ name: 'tax', value: val > 0 ? formatDistributedValue(val) : '' });
      }
      if (isMiscPerm && existingMisc === undefined) {
        const val = miscProportions[proportionIdx] ?? 0;
        newRow.push({
          name: 'miscellaneous_amount',
          value: val > 0 ? formatDistributedValue(val) : ''
        });
      }
      if (isDiscountPerm && existingDiscount === undefined) {
        const val = discountProportions[proportionIdx] ?? 0;
        newRow.push({ name: 'discount', value: val > 0 ? formatDistributedValue(val) : '' });
      }
      return newRow;
    }) ?? withoutTaxRow
  );
};

// The number in the PO box is a COMMITMENT number, and a commitment is a purchase
// order OR a subcontract — which one a vendor prints there is a property of their
// contract, not something this app can tell by looking. Tier B answers it: every
// PO-existence / match / fetch response carries `recordType`, and it writes the
// matching Vista ItemType onto the document at population.
export const COMMITMENT_RECORD_TYPE = { po: 'Po', subContract: 'subContract' };

// The Vista ItemType a commitment of this kind implies: 6 for a purchase order,
// 7 for a subcontract. Read off the company's own dropdown options so the value is
// always one the field can display and re-select; the literals are the fallback for
// a company whose option list has not loaded yet.
export const resolveCommitmentTypeOption = (typeOptions, recordType) => {
  const isSubcontract = recordType === COMMITMENT_RECORD_TYPE.subContract;
  const prefix = isSubcontract ? /^7/ : /^6/;
  return typeOptions?.find((t) => prefix.test(String(t))) || (isSubcontract ? '7-SL' : '6-PO');
};

// The Vista ItemType the DOCUMENT itself already carries, off whichever copy the response
// put it in. Tier B writes it at population the moment the commitment resolves, so on any
// document that matched one this is the answer — available on the document response, with
// no round trip to wait for. '-' is OCR's "not found" sentinel and counts as absent.
export const resolveDocumentCommitmentType = (document) => {
  const fromStandardized = String(document?.standardizeContent?.type ?? '').trim();
  if (fromStandardized && fromStandardized !== '-') return fromStandardized;
  const cell = (document?.codeContent || []).find((item) => item?.name === 'type');
  const fromCell = String(cell?.value ?? '').trim();
  return fromCell && fromCell !== '-' ? fromCell : '';
};

// Vista with the `type` field on: when a commitment number is present, default any row
// whose `type` is empty. Rows that already have a type keep it.
//
// `headerType` is the document's own ItemType and WINS over the kind-based guess — a line
// with no type of its own is the header's type, which is exactly the item-then-header
// precedence the Vista batch builder applies. It is what makes a subcontract render as
// 7-SL on load instead of flashing 6-PO until the existence check answers.
export const applyVistaPoTypeDefaultsToLineItems = (
  rawLineItems,
  poValue,
  typeOptions,
  recordType,
  headerType
) => {
  const hasPo = poValue && poValue !== '-' && poValue !== 'N/A' && String(poValue).trim() !== '';
  if (!hasPo) return rawLineItems;

  const defaultPoType =
    (headerType && String(headerType).trim()) ||
    resolveCommitmentTypeOption(typeOptions, recordType);
  return rawLineItems?.map((row) => {
    const typeField = row?.find((f) => f?.name === 'type');
    if (typeField && String(typeField.value || '').trim() !== '') return row; // already set
    const withoutType = row?.filter((f) => f?.name !== 'type');
    return [...withoutType, { name: 'type', value: defaultPoType }];
  });
};

// Swap one `type` value for another across every line-item row.
//
// The default above is applied on load, BEFORE the PO-existence call has answered — so it
// can only guess, and it guesses purchase order. This is how the guess is corrected once
// the ERP says the number is a subcontract. Only cells holding exactly `fromValue` move,
// and the array is returned unchanged when nothing does, so it is safe to feed straight
// back into state.
export const replaceLineItemTypeValue = (rawLineItems, fromValue, toValue) => {
  if (!Array.isArray(rawLineItems) || !fromValue || fromValue === toValue) return rawLineItems;
  let changed = false;
  const next = rawLineItems.map((row) => {
    if (!Array.isArray(row)) return row;
    const typeIdx = row.findIndex((f) => f?.name === 'type');
    if (typeIdx === -1 || row[typeIdx]?.value !== fromValue) return row;
    changed = true;
    const updated = [...row];
    updated[typeIdx] = { ...updated[typeIdx], value: toValue };
    return updated;
  });
  return changed ? next : rawLineItems;
};

// The codes get-default-codes can return besides the GL account. Payment terms, due date
// and the rest of the content fields are NOT codes — a vendor default that carries only a
// GL account plus a payment term is still an expense coding.
const NON_GL_DEFAULT_CODE_NAMES = [
  'userJobId',
  'userPhases',
  'userCostId',
  'userCostType',
  'equipmentNo',
  'serviceCode',
  'workOrder',
  'scope'
];

const hasCodeValue = (value) => {
  const text = String(value ?? '').trim();
  return text !== '' && text !== '-';
};

// True when the vendor's default coding (the `{name: value}` map built from the
// get-default-codes response) resolved a GL account and no other code. That is an
// expense coding, so the Vista Type is 3-Exp.
export const isGlOnlyDefaultCoding = (defaults) =>
  !!defaults &&
  hasCodeValue(defaults.GlAccount) &&
  NON_GL_DEFAULT_CODE_NAMES.every((name) => !hasCodeValue(defaults[name]));

// The Vista Expense ItemType, read off the company's own dropdown options so the value
// is always one the field can display and re-select; the literal is the fallback for a
// company whose option list has not loaded yet. Same approach as
// resolveCommitmentTypeOption.
export const resolveExpenseTypeOption = (typeOptions) =>
  typeOptions?.find((t) => /^3/.test(String(t))) || '3-Exp';

// Set `value` on every line-item row whose `type` is empty (inserting the cell where a
// row has none). Rows that already carry a type keep it. Returns the array unchanged
// when nothing moves, so it is safe to feed straight back into state.
export const fillEmptyLineItemTypeValue = (rawLineItems, value) => {
  if (!Array.isArray(rawLineItems) || !value) return rawLineItems;
  let changed = false;
  const next = rawLineItems.map((row) => {
    if (!Array.isArray(row)) return row;
    const typeIdx = row.findIndex((f) => f?.name === 'type');
    if (typeIdx !== -1 && String(row[typeIdx]?.value ?? '').trim() !== '') return row;
    changed = true;
    if (typeIdx === -1) return [...row, { name: 'type', value }];
    const updated = [...row];
    updated[typeIdx] = { ...updated[typeIdx], value };
    return updated;
  });
  return changed ? next : rawLineItems;
};

// Coding fields are validated separately from "real" data fields. This filters a
// per-row validation-error map down to only the non-coding errors.
const VISTA_CODING_FIELDS = [
  'userPhases',
  'userCostType',
  'scope',
  'workOrder',
  'GlAccount',
  'glAccount',
  'userCostId',
  'userJobId',
  'itemReference',
  'type'
];

export const getNonCodingLineItemErrors = (validationLineItemsErrors = {}) => {
  const nonCodingErrors = {};
  Object.entries(validationLineItemsErrors)?.forEach(([idx, errors]) => {
    const filtered = Object.entries(errors)?.filter(
      ([field]) => !VISTA_CODING_FIELDS?.includes(field)
    );
    if (filtered?.length > 0) nonCodingErrors[idx] = Object.fromEntries(filtered);
  });
  return nonCodingErrors;
};

// Same split as above, for the flat header (Account Details) error map — `{ field: message }`.
// Used to gate the Manual Approver button, which must stay reachable when the only blockers
// are coding errors that exist purely because the current user is the last approver.
export const getNonCodingHeaderErrors = (validationErrors = {}) =>
  Object.fromEntries(
    Object.entries(validationErrors)?.filter(([field]) => !VISTA_CODING_FIELDS?.includes(field))
  );

// Build the column-oriented line item object (`obj`) and the running list of column
// names from the raw `lineItem` rows. Vista pads `type` / `tax_code` / `tax_type`
// columns and preserves any non-empty `type` values already present in `prevObj`.
export const buildLineItemObj = ({
  lineItem,
  namesArray,
  erpType,
  typeFieldEnabled,
  displayTaxAsColumn,
  discountAsColumn = false,
  prevObj
}) => {
  const isVista = erpType === 'viewPointVista';
  let newNamesArray = [...namesArray];
  const arragedData = arrangedLineitems(lineItem);

  arragedData?.map((items) => {
    items?.map((element) => {
      if (isEmpty(newNamesArray)) {
        newNamesArray.push(element?.name);
      } else if (!newNamesArray?.includes(element?.name)) {
        newNamesArray.push(element?.name);
      }
    });
  });

  // Vista: ensure the type / tax_code / tax_type / discount columns are part of the shape.
  if (isVista && typeFieldEnabled && !newNamesArray?.includes('type')) newNamesArray.push('type');
  if (isVista && displayTaxAsColumn && !newNamesArray?.includes('tax_code'))
    newNamesArray.push('tax_code');
  if (isVista && displayTaxAsColumn && !newNamesArray?.includes('tax_type'))
    newNamesArray.push('tax_type');
  if (isVista && discountAsColumn && !newNamesArray?.includes('discount'))
    newNamesArray.push('discount');

  // With display-tax-as-column off, tax is not a line-item column — drop any tax
  // fields that arrived on the raw rows so the consolidated row shows no tax column.
  if (!displayTaxAsColumn) {
    newNamesArray = newNamesArray?.filter(
      (name) => name !== 'tax' && name !== 'tax_code' && name !== 'tax_type'
    );
  }

  // Discount is a line-item column only when the gate is on — drop any discount field
  // that arrived on the raw rows (e.g. legacy saved rows) so it doesn't show otherwise.
  if (!discountAsColumn) {
    newNamesArray = newNamesArray?.filter((name) => name !== 'discount');
  }

  const initialObj = newNamesArray?.reduce((accumulator, value) => {
    return { ...accumulator, [value]: [] };
  }, {});

  arragedData?.forEach((items, rowIdx) => {
    let newArray = [];
    items?.forEach((element) => {
      if (!element?.name) return;
      // Skip fields with no column (e.g. tax columns dropped when the permission is off).
      if (!Array.isArray(initialObj[element?.name])) return;
      initialObj[element?.name].push(element?.value);
      newArray.push(element?.name);
    });
    // Pad the type column — read from raw lineItem row so values set by
    // documentButtons (e.g. "6-PO" / "7-SC" for new rows) are not lost.
    if (isVista && typeFieldEnabled && !newArray.includes('type')) {
      const rawTypeValue = lineItem?.[rowIdx]?.find?.((f) => f?.name === 'type')?.value ?? '';
      initialObj['type'].push(rawTypeValue);
    }
    // Pad the tax_code column — preserve any pre-existing tax_code from the raw row.
    if (isVista && displayTaxAsColumn && !newArray.includes('tax_code')) {
      const rawTaxCode = lineItem?.[rowIdx]?.find?.((f) => f?.name === 'tax_code')?.value ?? '';
      initialObj['tax_code'].push(rawTaxCode);
    }
    // Pad the tax_type column — preserve any pre-existing tax_type from the raw row.
    if (isVista && displayTaxAsColumn && !newArray.includes('tax_type')) {
      const rawTaxType = lineItem?.[rowIdx]?.find?.((f) => f?.name === 'tax_type')?.value ?? '';
      initialObj['tax_type'].push(rawTaxType);
    }
    // Pad the discount column — preserve any pre-existing discount from the raw row.
    if (isVista && discountAsColumn && !newArray.includes('discount')) {
      const rawDiscount = lineItem?.[rowIdx]?.find?.((f) => f?.name === 'discount')?.value ?? '';
      initialObj['discount'].push(rawDiscount);
    }
  });

  // Preserve existing type values — the fetched PO line items send type as ""
  // which would wipe whatever the user had already set on each row.
  if (isVista && typeFieldEnabled && Array.isArray(initialObj['type'])) {
    initialObj['type'] = initialObj['type']?.map((v, idx) => {
      if (v && String(v).trim() !== '') return v;
      const existing = prevObj?.type?.[idx];
      return existing && String(existing).trim() !== '' ? existing : v;
    });
  }

  return { newNamesArray, initialObj };
};

// Options for the Manual Approver dropdowns (`manualApprovalSelections.jsx`).
//
// Two rules, in order:
//   1. Only signed-up company users who can actually approve — AP-side roles
//      (Accountant / Support) never appear as manual approvers.
//   2. Never anyone already on the document's `approvers` array. That array is the
//      audit trail the backend pushes an email onto on every approve / reject, so a
//      name still in the list has already acted on this document; offering them again
//      sends it back to someone who is done with it.
//
// `documentApprovers` is a flat array of emails as stored, but it is tolerant of the
// nested / object shapes the approval endpoints return so a caller can pass either.
export const getManualApproverOptions = (users = [], documentApprovers = []) => {
  const acted = new Set();
  const collect = (entry) => {
    if (!entry) return;
    if (Array.isArray(entry)) return entry.forEach(collect);
    if (typeof entry === 'string') {
      const email = entry.trim().toLowerCase();
      if (email) acted.add(email);
      return;
    }
    collect(entry?.userEmail ?? entry?.email);
  };
  collect(documentApprovers);

  return (users || [])
    ?.filter(
      (item) => item?.role !== 'Accountant' && item?.role !== 'Support' && item?.signUp === true
    )
    ?.map((item) => item?.email)
    ?.filter((email) => email && !acted.has(String(email).trim().toLowerCase()));
};
